const GO_API_BASE = (process.env.NEXT_PUBLIC_GO_API_URL ?? "").replace(
  /\/+$/,
  "",
);

type GoApiOptions = {
  method?: string;
  token?: string | null;
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
};

type GoApiResponse = {
  ok: boolean;
  status: number;
  json: () => Promise<any>;
};

function buildHeaders(options: GoApiOptions): Headers {
  const headers = new Headers(options.headers);

  if (options.token) {
    headers.set("Authorization", `Bearer ${options.token}`);
  }

  if (
    !(options.body instanceof FormData) &&
    !headers.has("Content-Type") &&
    options.body !== undefined
  ) {
    headers.set("Content-Type", "application/json");
  }

  return headers;
}

function buildBody(options: GoApiOptions): BodyInit | undefined {
  if (options.body === undefined) return undefined;
  if (options.body instanceof FormData) return options.body;
  return JSON.stringify(options.body);
}

/**
 * Calls Go directly (same-origin via nginx's /v1/ proxy). Returns a
 * Response-shaped object so most call sites only need to swap their
 * `fetch("/api/...")` call and stop hand-building headers — existing
 * `if (!response.ok) setError(data.error)` code keeps working unchanged.
 */
export async function goApiFetch(
  path: string,
  options: GoApiOptions = {},
): Promise<GoApiResponse> {
  const url = `${GO_API_BASE}${path}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? "GET",
      headers: buildHeaders(options),
      body: buildBody(options),
      signal: options.signal,
      cache: "no-store",
    });
  } catch (error) {
    console.error("Go API request error:", error);
    return {
      ok: false,
      status: 503,
      json: async () => ({
        error: "Unable to connect to server. Please try again later.",
      }),
    };
  }

  let parsed: Promise<any> | undefined;
  return {
    ok: response.ok,
    status: response.status,
    json: async () => {
      if (!parsed) {
        parsed = response.json().catch(() => ({ error: "Request failed" }));
      }
      return parsed;
    },
  };
}
